"use client";

import { useEffect, useRef, useState } from "react";

import { Paper, PaperProps, type CSSProperties } from "@mantine/core";
import clsx from "clsx";

import classes from "./ShadowRevealPaper.module.css";

/** Portion of the card that must be visible before the reveal arms. */
const REVEAL_RATIO = 0.4;

export interface ShadowRevealPaperProps extends Omit<PaperProps, "shadow"> {
  /** Mantine shadow token to settle into once revealed - defaults to sm */
  shadow?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Pause after entering the viewport before the shadow fades in, in ms */
  revealDelay?: number;
  children?: React.ReactNode;
}

/**
 * Paper whose shadow fades in only after the card has sat in the viewport
 * for a moment. Scrolling fully past resets it so the settle replays on the
 * next pass; quick scroll-throughs never trigger it at all.
 */
export function ShadowRevealPaper({
  shadow = "sm",
  revealDelay = 1200,
  className,
  style,
  children,
  ...props
}: ShadowRevealPaperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  // The observer is driven directly rather than through a hook that reports the latest entry as a
  // value, because whether the card is revealed depends on the entries that came before: it arms
  // at the reveal ratio and only disarms once the card is fully offscreen.
  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= REVEAL_RATIO) {
          setIsRevealed(true);
        } else if (!entry.isIntersecting) {
          // Only reset once fully offscreen, so the shadow never visibly drops
          // off a card that is still partially in view.
          setIsRevealed(false);
        }
      },
      { threshold: [0, REVEAL_RATIO] },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <Paper
      ref={ref}
      className={clsx(classes.reveal, className)}
      data-revealed={isRevealed || undefined}
      style={
        {
          "--reveal-shadow": `var(--mantine-shadow-${shadow})`,
          "--reveal-delay": `${revealDelay}ms`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {children}
    </Paper>
  );
}
